/**
 * Files the agent panel accepts (F-144): text the server can parse as a
 * table — markdown, CSV/TSV, plain text. Read in the browser, sent as text;
 * nothing is uploaded as an asset, and nothing is kept after the turn.
 */
import { AGENT_MAX_ATTACHMENT_CHARS, type AgentAttachment } from "@kola/shared";

/** The file picker's `accept`, and the extensions a drop is checked against. */
export const AGENT_ATTACHMENT_ACCEPT = ".md,.markdown,.csv,.tsv,.txt";
const EXTENSIONS = AGENT_ATTACHMENT_ACCEPT.split(",");

export function isAgentAttachmentFile(file: { name: string; type?: string }): boolean {
  const lower = file.name.toLowerCase();
  if (EXTENSIONS.some((ext) => lower.endsWith(ext))) return true;
  return file.type?.startsWith("text/") === true;
}

export type ReadAttachmentOutcome =
  | { ok: true; attachment: AgentAttachment }
  | { ok: false; reason: "type" | "empty" | "too-large" };

export async function readAgentAttachment(file: File, id: string): Promise<ReadAttachmentOutcome> {
  if (!isAgentAttachmentFile(file)) return { ok: false, reason: "type" };
  return checkText(file.name, await readFileText(file), id);
}

/**
 * The same checks for text already read elsewhere — a file from the host's
 * own folders (F-153), which arrives as text, not as a `File`.
 */
export function agentAttachmentFromText(name: string, text: string, id: string): ReadAttachmentOutcome {
  if (!isAgentAttachmentFile({ name })) return { ok: false, reason: "type" };
  return checkText(name, text, id);
}

function checkText(name: string, text: string, id: string): ReadAttachmentOutcome {
  if (text.trim().length === 0) return { ok: false, reason: "empty" };
  if (text.length > AGENT_MAX_ATTACHMENT_CHARS) return { ok: false, reason: "too-large" };
  return { ok: true, attachment: { id, name, text } };
}

/**
 * `File.text()` where it exists; `FileReader` where it does not (older
 * WebViews, and jsdom — the desktop app's dialog specs run there).
 */
export function readFileText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error ?? new Error("could not read the file"));
    reader.readAsText(file);
  });
}

export function attachmentProblem(reason: "type" | "empty" | "too-large", name: string): string {
  switch (reason) {
    case "type":
      return `"${name}" isn't a text file — attach a .md, .csv, .tsv or .txt.`;
    case "empty":
      return `"${name}" is empty.`;
    case "too-large":
      return `"${name}" is too large — the limit is ${Math.round(AGENT_MAX_ATTACHMENT_CHARS / 1000)}k characters.`;
  }
}
